import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
// Redux
import { useSelector } from "react-redux";
// Constants - Functions
import { pathHomePage, protocols, prescriptionColors1, prescriptionColors2 } from "../../../../config/constants";
import { parseSecondsToMinutes, renderToolTip } from "../../../../config/functions";
import { isPhraseProjectHospital, isNestProjectHospital } from "../../../../config/projectFunctions";
import { isClinicOrHome, isAppOrPlus, isPlus } from "../../../../config/validateApplication";
import { validateApplicationProtocol, isAppProtocol, isProtocolWithControls } from "../../../../config/validateProtocols";
// Components
import {
  PrescriptionDiv,
  ProtocolCardDiv,
  VideoButton,
  ProtocolImgAndNameDiv,
  ProtocolImgDiv,
  ProtocolImg,
  MinutesAdherenceDiv,
  MinutesAdherenceText,
  VideoButtonImg,
  InfoImg,
  ProtocolName,
  ShowPrescriptionsButton,
  ArrowImg,
  PrescriptionDaysDiv,
  FlexDiv,
  SelectAllButton,
  SelectAllImg,
  DayText,
  InputCheckboxDiv,
  InputCheckbox,
  VerifyCheckboxImg,
  CheckboxDiv,
  CheckboxOrderText,
  ExtraInfoDiv,
  ExtraInfoText,
  DurationSelect,
  ReportsSectionDiv,
  SessionsDiv,
  ReportsLink,
  ReportsImg,
  SessionsNumber,
  SupportDiv,
  SupportText,
  PlatformsDiv,
  PlatformsAlignerDiv,
  PlatformsImg,
  ProtocolControlsDiv,
  ProtocolControlsButton,
  ProtocolControlsImg,
  ARIncompatibilityImg,
} from "./styles";
import ProtocolVideoPopup from "../../../protocolVideoPopup";
import Popup from "../../../popup";
import ProtocolControls from "../protocolControls";
// Assets
import IcoArrowUp from "../../../../assets/icons/ico_arrow_up.svg";
import IcoArrowDown from "../../../../assets/icons/ico_arrow_down.svg";
import IcoOpenVideo from "../../../../assets/icons/ico_video.svg";
import IcoInfo from "../../../../assets/icons/ico_info_blue.svg";
import CheckboxAllBlue from "../../../../assets/icons/ico_checkbox_full_blue.svg";
import CheckboxEmptyBlue from "../../../../assets/icons/ico_checkbox_empty_blue.svg";
import CheckboxAllGray from "../../../../assets/icons/ico_checkbox_full_gray.svg";
import CheckboxEmptyGray from "../../../../assets/icons/ico_checkbox_empty_gray.svg";
import CheckDisabled from "../../../../assets/icons/ico_checkbox_disabled.svg";
import CheckTrue from "../../../../assets/icons/ico_checkbox_true.svg";
import BonusCheckTrue from "../../../../assets/icons/ico_bonusCheckbox_true.svg";
import BonusCheckDisabled from "../../../../assets/icons/ico_bonusCheckbox_disabled.svg";
import CheckFalse from "../../../../assets/icons/ico_checkbox_false.svg";
import ReportsIco from "../../../../assets/icons/ico_reports.svg";
import PhoneImg from "../../../../assets/icons/platforms_phone.svg";
import TabletImg from "../../../../assets/icons/platforms_tablet.svg";
import PhoneAndTabletImg from "../../../../assets/icons/platforms_phone_and_tablet.svg";
import WebImg from "../../../../assets/icons/platforms_web.svg";
import EditIcon from "../../../../assets/icons/btn_edit_user_blue.svg";
import WarningImg from "../../../../assets/icons/ico_warning.svg";

const ProtocolPrescription = ({
  protocolId,
  protocolImg,
  protocolName,
  prescriptions,
  prescriptionsOrder = [],
  setPrescriptionsOrder,
  isDisabledInputs,
  prescriptionsToDatabase,
  patientId,
  software,
  sessionsNumber,
  minutesUsage,
  totalPrescriptions,
  videoLink,
  protocolDescription,
  protocolPlatforms = [],
  protocolControlsToSave,
  setProtocolControlsToSave,
  protocolControlsErrors,
  setProtocolControlsErrors,
  rechargeProtocolControls,
  setRechargeProtocolControls,
  isIncompatibleWithAR,
  protocolWithSupport,
  isColorToggleActive,
}) => {
  const [monday, setMonday] = useState(false);
  const [tuesday, setTuesday] = useState(false);
  const [wednesday, setWednesday] = useState(false);
  const [thursday, setThursday] = useState(false);
  const [friday, setFriday] = useState(false);
  const [saturday, setSaturday] = useState(false);
  const [sunday, setSunday] = useState(false);
  const [bonus, setBonus] = useState(false);
  const [duration, setDuration] = useState(5);
  const [isProtocolVideoShowed, setIsProtocolVideoShowed] = useState(false);
  const [isShowPrescriptions, setIsShowPrescriptions] = useState(false);
  const [isShowingControlsPopup, setIsShowingControlsPopup] = useState(false);
  const [isPHRASEOrNESTHospital, setIsPHRASEOrNESTHospital] = useState(false);
  const [isARProtocol] = useState(validateApplicationProtocol(protocols.app.ar, protocolId));
  const clinicianToken = useSelector(({ clinician }) => clinician.data.token);
  const hospitalName = useSelector(({ clinician }) => clinician.data.HOSPITAL_NAME);
  const [t] = useTranslation("global");

  // Setting prescription days
  useEffect(() => {
    if (prescriptions.length || prescriptionsToDatabase.length) {
      if (prescriptions.length && duration === 5 && prescriptions[0]) {
        setDuration(parseSecondsToMinutes(prescriptions[0].SESSION_DURATION));
      }

      const daysToSet = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY", "BONUS"];

      const setDays = (weekday, setDay) => {
        const draftPrescription = prescriptionsToDatabase.find((p) => p.PROTOCOL_ID === protocolId && p.WEEKDAY === weekday);

        const originalPrescription = prescriptions.find((p) => p.WEEKDAY === weekday && p.PROTOCOL_ID === protocolId);
        if (draftPrescription) {
          setDay(draftPrescription.hasOwnProperty("SESSION_DURATION"));
        } else if (originalPrescription) {
          setDay(true);
        } else {
          setDay(false);
        }
      };

      daysToSet.forEach((day, index) => {
        setDays(day, [setMonday, setTuesday, setWednesday, setThursday, setFriday, setSaturday, setSunday, setBonus][index]);
      });
    }
  }, [prescriptions, prescriptionsToDatabase, prescriptionsOrder, protocolId, duration]);

  // Check hospital project
  useEffect(() => {
    const checkHospital = async () => {
      const isPHRASE = await isPhraseProjectHospital(hospitalName);
      const isNEST = await isNestProjectHospital(hospitalName);
      setIsPHRASEOrNESTHospital(isPHRASE || isNEST);
    };
    checkHospital();

    return () => {
      setIsPHRASEOrNESTHospital(false);
    };
  }, [hospitalName]);

  const handleShowPrescriptions = (e) => {
    e.preventDefault();
    setIsShowPrescriptions(!isShowPrescriptions);
  };

  const renderProtocolControlsButton = () => {
    const openProtocolControlsPopup = (e) => {
      e.preventDefault();
      setProtocolControlsErrors(false);
      setIsShowingControlsPopup(true);
    };

    return (
      <ProtocolControlsButton onClick={openProtocolControlsPopup} border={protocolControlsErrors ? "2px solid red" : "none"}>
        <ProtocolControlsImg src={EditIcon} alt="edit-icon" />
      </ProtocolControlsButton>
    );
  };

  const renderProtocolControlsPopup = () => {
    return (
      <Popup isShowed={isShowingControlsPopup} setIsShowed={setIsShowingControlsPopup}>
        <ProtocolControls
          patientId={patientId}
          protocolId={protocolId}
          software={software}
          protocolControlsToSave={protocolControlsToSave}
          setProtocolControlsToSave={setProtocolControlsToSave}
          setIsShowingControlsPopup={setIsShowingControlsPopup}
          rechargeProtocolControls={rechargeProtocolControls}
          setRechargeProtocolControls={setRechargeProtocolControls}
        />
      </Popup>
    );
  };

  const renderDurationOptions = () => {
    const minutes = Array.from({ length: 10 }, (_, i) => (
      <option key={i + 1} value={i + 1}>
        {i + 1} min
      </option>
    ));

    const handleSetDuration = (min) => {
      setDuration(min);
      let activeDays = [];

      if (monday) activeDays.push("MONDAY");
      if (tuesday) activeDays.push("TUESDAY");
      if (wednesday) activeDays.push("WEDNESDAY");
      if (thursday) activeDays.push("THURSDAY");
      if (friday) activeDays.push("FRIDAY");
      if (saturday) activeDays.push("SATURDAY");
      if (sunday) activeDays.push("SUNDAY");
      if (bonus) activeDays.push("BONUS");

      for (const day of activeDays) {
        const verifyPrescription = prescriptionsToDatabase.find(
          (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day,
        );

        if (verifyPrescription) {
          prescriptionsToDatabase.forEach((prescription) => {
            if (prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day) {
              prescription.SESSION_DURATION = Number(min) * 60;
              if (isAppOrPlus(software)) prescription.AR_MODE = isARProtocol ? "TABLE" : "NONE";
            }
          });
        } else {
          const prescription = {
            PROTOCOL_ID: protocolId,
            WEEKDAY: day,
            STARTING_DATE: new Date(),
            ENDING_DATE: new Date("2100-01-01"),
            SESSION_DURATION: Number(min) * 60,
          };
          if (isAppOrPlus(software)) prescription.AR_MODE = isARProtocol ? "TABLE" : "NONE";

          prescriptionsToDatabase.push(prescription);
        }
      }
    };

    const isSetDurationDisabled = () => {
      if (isDisabledInputs) return true;
      if (isProtocolWithControls(protocolId)) return true;
      return ![monday, tuesday, wednesday, thursday, friday, saturday, sunday, bonus].some(Boolean);
    };

    return (
      <>
        {isPlus(software) && isProtocolWithControls(protocolId) ? (
          renderProtocolControlsButton()
        ) : (
          <DurationSelect value={duration} onChange={(e) => handleSetDuration(e.target.value)} disabled={isSetDurationDisabled()}>
            {minutes}
          </DurationSelect>
        )}
      </>
    );
  };

  const validatePrescriptionsNumberPerDay = (day) => {
    const limitOfProtocolsPerDay = 7;

    const dbPrescriptions = totalPrescriptions.filter((prescription) => prescription.WEEKDAY === day);
    const prescriptionsDayToSave = prescriptionsToDatabase.filter((prescription) => prescription.WEEKDAY === day);

    let currentPrescriptions = [];

    for (const dbPrescription of dbPrescriptions) {
      const validatedPrescription = prescriptionsDayToSave.find(
        (prescriptionDayToSave) =>
          prescriptionDayToSave.PROTOCOL_ID === dbPrescription.PROTOCOL_ID &&
          prescriptionDayToSave.WEEKDAY === dbPrescription.WEEKDAY &&
          !prescriptionDayToSave.STARTING_DATE,
      );
      if (!validatedPrescription) currentPrescriptions.push(dbPrescription);
    }

    for (const prescriptionDayToSave of prescriptionsDayToSave) {
      if (prescriptionDayToSave.STARTING_DATE) {
        const validatedPrescription = currentPrescriptions.find(
          (prescription) => prescription.PROTOCOL_ID === prescriptionDayToSave.PROTOCOL_ID,
        );
        if (!validatedPrescription) currentPrescriptions.push(prescriptionDayToSave);
      }
    }

    return currentPrescriptions.length >= limitOfProtocolsPerDay;
  };

  const handleSetCheckbox = (setDay, isActiveDay, day, skipOrderUpdate = false) => {
    setDay(!isActiveDay);

    const prescriptionVerification = prescriptionsToDatabase.find(
      (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day,
    );

    if (isClinicOrHome(software)) {
      if (!skipOrderUpdate) {
        const prescriptionOrderVerification = prescriptionsOrder.find(
          (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day,
        );

        if (!prescriptionOrderVerification && !isActiveDay) {
          setPrescriptionsOrder((prev) => [...prev, { PROTOCOL_ID: protocolId, WEEKDAY: day }]);
        }
        if (prescriptionOrderVerification && isActiveDay) {
          setPrescriptionsOrder((prev) =>
            prev.filter((prescription) => !(prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day)),
          );
        }
      }
    }

    // Add a new prescription
    if (!prescriptionVerification && !isActiveDay) {
      let prescription = {
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
        STARTING_DATE: new Date(),
        ENDING_DATE: new Date("2100-01-01"),
        SESSION_DURATION: Number(duration) * 60,
      };
      if (isAppOrPlus(software)) {
        prescription.AR_MODE = isARProtocol ? "TABLE" : "NONE";
      }
      return prescriptionsToDatabase.push(prescription);
    }
    // Removing prescription but exists in database
    if (!prescriptionVerification && isActiveDay) {
      return prescriptionsToDatabase.push({
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
      });
    }
    // Add prescription that previously existed locally
    if (prescriptionVerification && !isActiveDay) {
      const prescriptionToRemove = prescriptionsToDatabase.findIndex(
        (prescription) => prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day,
      );
      prescriptionsToDatabase.splice(prescriptionToRemove, 1);
      let prescription = {
        PROTOCOL_ID: protocolId,
        WEEKDAY: day,
        STARTING_DATE: new Date(),
        ENDING_DATE: new Date("2100-01-01"),
        SESSION_DURATION: Number(duration) * 60,
      };
      if (isAppOrPlus(software)) {
        prescription.AR_MODE = isARProtocol ? "TABLE" : "NONE";
      }
      return prescriptionsToDatabase.push(prescription);
    }
    // Remove prescription that previously existed locally
    if (prescriptionVerification && isActiveDay) {
      prescriptionsToDatabase.forEach((prescription) => {
        if (prescription.PROTOCOL_ID === protocolId && prescription.WEEKDAY === day) {
          delete prescription.STARTING_DATE;
          delete prescription.ENDING_DATE;
          delete prescription.SESSION_DURATION;
          delete prescription.AR_MODE;
        }
      });
    }
  };

  const setOneCheckbox = (setDay, isActiveDay, day) => {
    if (isClinicOrHome(software) && !isActiveDay && validatePrescriptionsNumberPerDay(day) && day !== "BONUS") {
      return toast.error(t("prescriptions_protocolsLimitMessage"), {
        toastId: "prescriptionLimitReached",
      });
    }
    handleSetCheckbox(setDay, isActiveDay, day);
  };

  const isAllPrescriptionsSelected = () => {
    return ![monday, tuesday, wednesday, thursday, friday, saturday, sunday].some((day) => !day);
  };

  const renderProtocolCard = () => {
    const handleShowProtocolVideo = (e) => {
      e.preventDefault();
      setIsProtocolVideoShowed(true);
    };

    const renderARIncompatibilityWarning = () => {
      return (
        <>
          <ARIncompatibilityImg data-for={`warning${protocolId}`} data-tip={t("ar_incompatibility")} src={WarningImg} alt="ico-warning" />
          {renderToolTip(`warning${protocolId}`)}
        </>
      );
    };

    return (
      <ProtocolCardDiv isClinicOrHome={isClinicOrHome(software)} isARSection={isARProtocol}>
        {isAppOrPlus(software) ? (
          <>
            <VideoButton onClick={handleShowProtocolVideo} disabled={!videoLink}>
              <ProtocolImgDiv>
                <ProtocolImg src={protocolImg} alt="protocol-img" />
                {isIncompatibleWithAR && renderARIncompatibilityWarning()}
                {isPHRASEOrNESTHospital && isAppProtocol(protocolId) && (
                  <MinutesAdherenceDiv data-for="minutesUsagePrompt" data-tip={t("prescriptions_minutesUsage")}>
                    <MinutesAdherenceText>{minutesUsage}</MinutesAdherenceText>
                    {renderToolTip("minutesUsagePrompt")}
                  </MinutesAdherenceDiv>
                )}
              </ProtocolImgDiv>

              {videoLink ? (
                <VideoButtonImg src={IcoOpenVideo} alt="ico-video" data-for="protocolInfoDescription" data-tip={protocolDescription} />
              ) : (
                <InfoImg src={IcoInfo} alt="ico-info" data-for="protocolInfoDescription" data-tip={protocolDescription} />
              )}
              {renderToolTip("protocolInfoDescription")}
              <ProtocolName>{protocolName}</ProtocolName>
            </VideoButton>
            {isProtocolVideoShowed && <ProtocolVideoPopup setIsProtocolVideoShowed={setIsProtocolVideoShowed} protocolId={protocolId} />}
          </>
        ) : (
          <ProtocolImgAndNameDiv onClick={handleShowProtocolVideo}>
            <ProtocolImg src={protocolImg} alt="protocol-img" />
            <ProtocolName>{protocolName}</ProtocolName>
          </ProtocolImgAndNameDiv>
        )}
        <ShowPrescriptionsButton onClick={handleShowPrescriptions}>
          <ArrowImg src={isShowPrescriptions ? IcoArrowUp : IcoArrowDown} alt="ico-arrow" />
        </ShowPrescriptionsButton>
      </ProtocolCardDiv>
    );
  };

  const renderSupportText = () => {
    return (
      <SupportDiv>
        <SupportText protocolWithSupport={protocolWithSupport}>{protocolWithSupport ? t("yes") : t("no")}</SupportText>
      </SupportDiv>
    );
  };

  const renderProtocolDays = () => {
    const days = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

    const dayValues = {
      MONDAY: [setMonday, monday],
      TUESDAY: [setTuesday, tuesday],
      WEDNESDAY: [setWednesday, wednesday],
      THURSDAY: [setThursday, thursday],
      FRIDAY: [setFriday, friday],
      SATURDAY: [setSaturday, saturday],
      SUNDAY: [setSunday, sunday],
    };

    const handleSetAllPrescriptions = (e) => {
      e.preventDefault();

      const daysData = [
        { label: "MONDAY", state: monday, setter: setMonday },
        { label: "TUESDAY", state: tuesday, setter: setTuesday },
        { label: "WEDNESDAY", state: wednesday, setter: setWednesday },
        { label: "THURSDAY", state: thursday, setter: setThursday },
        { label: "FRIDAY", state: friday, setter: setFriday },
        { label: "SATURDAY", state: saturday, setter: setSaturday },
        { label: "SUNDAY", state: sunday, setter: setSunday },
      ];

      if (isAllPrescriptionsSelected()) {
        daysData.forEach(({ label, state, setter }) => {
          handleSetCheckbox(setter, state, label, true);
        });

        isClinicOrHome(software) && setPrescriptionsOrder((prev) => prev.filter((p) => p.PROTOCOL_ID !== protocolId));
      } else {
        const newOrderItems = [];

        daysData.forEach(({ label, state, setter }) => {
          if (!state) {
            if (isClinicOrHome(software) && validatePrescriptionsNumberPerDay(label) && label !== "BONUS") {
              toast.error(t("prescriptions_protocolsLimitMessage"), {
                toastId: "prescriptionLimitReached",
              });
              return;
            }
            handleSetCheckbox(setter, state, label, true);
            newOrderItems.push({ PROTOCOL_ID: protocolId, WEEKDAY: label });
          }
        });
        if (isClinicOrHome(software) && newOrderItems.length > 0) {
          setPrescriptionsOrder((prev) => [...prev, ...newOrderItems]);
        }
      }
    };

    const getPriorityNumber = (day) => {
      const filteredPrescriptions = prescriptionsOrder.filter((prescription) => prescription.WEEKDAY === day);
      const prescriptionIndex = filteredPrescriptions.findIndex((prescription) => prescription.PROTOCOL_ID === protocolId);
      return prescriptionIndex + 1;
    };

    return (
      <PrescriptionDaysDiv show={isShowPrescriptions}>
        <FlexDiv height910="45px">
          <SelectAllButton onClick={handleSetAllPrescriptions} disabled={isDisabledInputs}>
            <SelectAllImg
              src={
                isDisabledInputs
                  ? isAllPrescriptionsSelected()
                    ? CheckboxAllGray
                    : CheckboxEmptyGray
                  : isAllPrescriptionsSelected()
                    ? CheckboxAllBlue
                    : CheckboxEmptyBlue
              }
              alt="select-all-prescriptions"
            />
          </SelectAllButton>
        </FlexDiv>
        {days.map((day) => {
          const [daySetter, dayValue] = dayValues[day];
          const backgroundColor = isColorToggleActive ? prescriptionColors2[day] : prescriptionColors1[day];

          return (
            <FlexDiv key={`${day}${protocolId}`}>
              <DayText htmlFor={`${day}${protocolId}`}>{t(`prescriptions_tableProtocolDays_${day.toLowerCase()}`)}</DayText>
              <InputCheckboxDiv>
                <InputCheckbox
                  type="checkbox"
                  id={`${day}${protocolId}`}
                  value={dayValue}
                  onClick={() => setOneCheckbox(daySetter, dayValue, day)}
                  disabled={isDisabledInputs}
                />
                {isClinicOrHome(software) ? (
                  !dayValue ? (
                    <VerifyCheckboxImg src={CheckFalse} alt="check-icon" />
                  ) : (
                    <CheckboxDiv background={isDisabledInputs ? "#999999" : backgroundColor}>
                      <CheckboxOrderText>{getPriorityNumber(day)}</CheckboxOrderText>
                    </CheckboxDiv>
                  )
                ) : (
                  <VerifyCheckboxImg
                    src={dayValue && isDisabledInputs ? CheckDisabled : dayValue ? CheckTrue : CheckFalse}
                    alt="check-icon"
                  />
                )}
              </InputCheckboxDiv>
            </FlexDiv>
          );
        })}
      </PrescriptionDaysDiv>
    );
  };

  const getSessionsNumber = () => {
    if (!sessionsNumber) return "-";
    if (sessionsNumber > 99) return "+99";
    return sessionsNumber;
  };

  const renderProtocolDaysResponsive = () => {
    return (
      <ExtraInfoDiv show={isShowPrescriptions} isClinicOrHome={isClinicOrHome(software)}>
        {isClinicOrHome(software) && (
          <FlexDiv width="20px" width910="60px">
            <DayText htmlFor="bonus">{t("prescriptions_tableBonusText")}</DayText>
            <InputCheckboxDiv>
              <InputCheckbox
                type="checkbox"
                id="bonus"
                value={bonus}
                onClick={() => handleSetCheckbox(setBonus, bonus, "BONUS")}
                disabled={isDisabledInputs}
              />
              {bonus && isDisabledInputs ? (
                <VerifyCheckboxImg src={BonusCheckDisabled} alt="check-disabled" />
              ) : bonus ? (
                <VerifyCheckboxImg src={BonusCheckTrue} alt="check-true" />
              ) : (
                <VerifyCheckboxImg src={CheckFalse} alt="check-false" />
              )}
            </InputCheckboxDiv>
          </FlexDiv>
        )}
        <ProtocolControlsDiv>
          <ExtraInfoText>{t("duration").toUpperCase()}</ExtraInfoText>
          {renderDurationOptions()}
        </ProtocolControlsDiv>
        <FlexDiv width="120px" width910="95px">
          <ReportsSectionDiv>
            <ExtraInfoText>{t("reports").toUpperCase()}</ExtraInfoText>
            <SessionsDiv isARSection={isARProtocol}>
              <ReportsLink
                sessions={sessionsNumber}
                to={`${pathHomePage}/reports/${clinicianToken}/${software}/${patientId}/${protocolId}`}
              >
                <ReportsImg src={ReportsIco} alt="reports-btn" />
              </ReportsLink>
              <SessionsNumber>{getSessionsNumber()}</SessionsNumber>
            </SessionsDiv>
          </ReportsSectionDiv>
        </FlexDiv>
      </ExtraInfoDiv>
    );
  };

  const renderProtocolPlatforms = () => {
    const getPlatformData = () => {
      if (protocolPlatforms.includes("MOBILE_PHONE") && protocolPlatforms.includes("MOBILE_TABLET")) {
        return { prompt: "platforms_smartphone_and_tablet", image: PhoneAndTabletImg };
      }
      if (protocolPlatforms.includes("MOBILE_PHONE")) {
        return { prompt: "platforms_smartphone", image: PhoneImg };
      }
      if (protocolPlatforms.includes("MOBILE_TABLET")) {
        return { prompt: "platforms_tablet", image: TabletImg };
      }
      return { prompt: null, image: null };
    };

    const mobileData = getPlatformData();
    const webImg = protocolPlatforms.includes("WEB_NONE") ? WebImg : null;

    return (
      <PlatformsDiv>
        <PlatformsAlignerDiv
          width="60px"
          isSomeImg={mobileData.image}
          data-for={`devicePrompt${protocolId}`}
          data-tip={t(mobileData.prompt)}
        >
          <PlatformsImg src={mobileData.image} alt="platform-img" />
          {mobileData.image && renderToolTip(`devicePrompt${protocolId}`)}
        </PlatformsAlignerDiv>
        <PlatformsAlignerDiv width="40px" isSomeImg={webImg} data-for={`webPrompt${protocolId}`} data-tip={t("platforms_web")}>
          <PlatformsImg src={webImg} alt="platform-img" />
          {webImg && renderToolTip(`webPrompt${protocolId}`)}
        </PlatformsAlignerDiv>
      </PlatformsDiv>
    );
  };

  return (
    <PrescriptionDiv>
      {renderProtocolCard()}
      {isARProtocol && renderSupportText()}
      {renderProtocolDays()}
      {renderProtocolDaysResponsive()}
      {isPlus(software) && renderProtocolPlatforms()}
      {isShowingControlsPopup && renderProtocolControlsPopup()}
    </PrescriptionDiv>
  );
};

export default ProtocolPrescription;
